import { inject } from '@angular/core';
import { CanActivateFn, Route, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { permissionGuard } from '../../guards/permission.guard';
import { AuthStore } from '../auth/store/auth.store';
import { FinancialsFoundationService } from './financials-foundation.service';
import { FinancialsPlaceholderComponent } from './financials-placeholder.component';
import { FinancialsWorkspaceComponent } from './financials-workspace.component';

const requireFinancialsAccess: CanActivateFn = () => {
  const auth = inject(AuthStore);
  const orgId = auth.activeOrganizationId();
  const denied = inject(Router).createUrlTree(['/home/403-forbidden']);
  if (!orgId) return denied;
  return inject(FinancialsFoundationService).access().pipe(
    map(access => auth.activeOrganizationId() === orgId
      && access.organizationId === orgId
      && access.operations.some(x => x.code === 'financials.access' && x.allowed)
      ? true : denied),
    catchError(() => of(denied)),
  );
};

const placeholder = (path: string, title: string, description: string): Route => ({
  path,
  component: FinancialsPlaceholderComponent,
  data: { breadcrumb: title, title, description },
});

export const financialsRoute: Route = {
  path: 'financials',
  data: { breadcrumb: 'Financials' },
  component: FinancialsWorkspaceComponent,
  canActivateChild: [permissionGuard('financials.access', { retryAfterSubscriptionRecovery: false }), requireFinancialsAccess],
  children: [
    { path: '', pathMatch: 'full', redirectTo: 'overview' },
    placeholder('overview', 'Overview', 'A starting point for the organisation’s managed Financials work.'),
    placeholder('accounts', 'Accounts', 'View the planned workspace for financial accounts and the chart of accounts.'),
    placeholder('banking', 'Banking', 'View the planned workspace for bank accounts and statement imports.'),
    placeholder('transactions', 'Transactions', 'View the planned workspace for reviewed financial transactions.'),
    placeholder('tax', 'Tax', 'View the planned workspace for tax obligations and returns.'),
    placeholder('reports', 'Reports', 'View the planned workspace for financial reports.'),
    {
      path: 'setup',
      data: { breadcrumb: 'Setup' },
      loadComponent: () => import('./financials-foundation.component').then(m => m.FinancialsFoundationComponent),
    },
    { path: '**', redirectTo: 'overview' },
  ],
};
